import { useMemo, useState, useCallback } from 'react';
import {
  ColumnTable,
  Table,
  Button,
  addToast,
  showDialogConfirm,
  Icon,
} from '@octano/global-ui';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Badge } from 'reactstrap';
import OfferModal from './AdmissionProcessOffers/AdmissionProcessOfferModal';
import { deleteOfferAction } from './managementActions';
import { AdmissionViaOption, AvailableOffer, Offer } from '../types';
import { getSelectedAdmissionScope } from '../utils';

interface Props {
  processId: number;
  offers: Offer[];
  availableOffers: AvailableOffer[];
  admissionVias: AdmissionViaOption[];
  refresh: () => void;
}

export default function ProcessOffersList({
  processId,
  offers,
  availableOffers,
  admissionVias,
  refresh,
}: Props) {
  const { t } = useTranslation('academic-offer/admission-processes');
  const [selectedOffer, setSelectedOffer] = useState<Offer | undefined>(
    undefined,
  );
  const history = useHistory();
  const [showModal, setShowModal] = useState(false);

  const toggleModal = useCallback((offer?: Offer) => {
    setSelectedOffer(offer);
    setShowModal((prev) => !prev);
  }, []);

  const handleDelete = useCallback(
    (offer: Offer) => {
      showDialogConfirm({
        icon: { name: 'warning', color: 'warning' },
        title: t('offersTable.deleteConfirm.title'),
        subtitle: t('offersTable.deleteConfirm.subtitle'),
        btnConfirm: {
          text: t('offersTable.deleteConfirm.confirm'),
          onConfirm: async () => {
            const result = await deleteOfferAction({
              admissionId: offer.admissionId,
            });
            addToast({
              text: result
                ? t('offersTable.deleteSuccess')
                : t('offersTable.deleteError'),
              color: result ? 'success' : 'danger',
            });
            if (result) {
              refresh();
            }
          },
        },
        btnClose: { text: t('offersTable.deleteConfirm.cancel') },
      });
    },
    [t, refresh],
  );

  const handleCopyLink = useCallback(
    async (url?: string) => {
      if (!url) {
        addToast({
          text: t('offersTable.copyLinkError'),
          color: 'danger',
        });
        return;
      }
      try {
        await navigator.clipboard.writeText(url);
        addToast({
          text: t('offersTable.linkCopied'),
          color: 'success',
        });
      } catch {
        addToast({
          text: t('offersTable.copyLinkError'),
          color: 'danger',
        });
      }
    },
    [t],
  );

  const separateQuotaViaIds = useMemo(
    () =>
      new Set(
        admissionVias.filter((v) => !v.deductsOfferQuota).map((v) => v.id),
      ),
    [admissionVias],
  );

  const columns = useMemo<ColumnTable<any>[]>(
    () => [
      {
        headerText: t('offersTable.studyPlan'),
        columnName: 'studyPlanName',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: t('offersTable.campus'),
        columnName: 'campusName',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: t('offersTable.schedule'),
        columnName: 'scheduleName',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: t('offersTable.quota'),
        columnName: 'quota',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      ...(admissionVias.length >= 1
        ? [
            {
              headerText: t('offersTable.admissionVia'),
              columnName: 'admissionViaName',
              thClassName: 'text-center',
              tdClassName: 'text-center',
              cellFormat: ({ row }: any) => {
                if (!row.admissionViaName) {
                  return '-';
                }
                if (!separateQuotaViaIds.has(row.admissionViaId)) {
                  return row.admissionViaName;
                }
                return (
                  <div className="d-flex flex-column align-items-center">
                    <span>{row.admissionViaName}</span>
                    <Badge color="info" pill className="mt-1 fs-12">
                      {t('offersTable.separateQuotaChip')}
                    </Badge>
                  </div>
                );
              },
            },
          ]
        : []),
      {
        headerText: t('offersTable.enrollmentType'),
        columnName: 'admissionScopes',
        thClassName: 'text-center max-w-200',
        tdClassName: 'text-center max-w-200',
        cellFormat: ({ row }: any) => {
          if (!row.admissionScopes || row.admissionScopes.length === 0) {
            return '-';
          }
          const scope = getSelectedAdmissionScope(row.admissionScopes, t);
          return scope?.label || '-';
        },
      },
      {
        headerText: t('offersTable.actions'),
        columnName: 'actions',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }: any) => (
          <div
            style={{ gap: 5 }}
            className="d-flex justify-content-end align-items-center"
          >
            <button
              aria-label={t('offersTable.edit')}
              type="button"
              className="btn btn-sm"
              onClick={() => toggleModal(row)}
            >
              <Icon name="edit" />
            </button>
            <button
              aria-label={t('offersTable.copyLink')}
              type="button"
              className="btn btn-sm"
              onClick={() => handleCopyLink(row?.postulantPortalURL)}
            >
              <Icon name="duplicate" />
            </button>
            {!row.isUsed && (
              <button
                aria-label={t('offersTable.delete')}
                type="button"
                className="btn btn-sm"
                onClick={() => handleDelete(row)}
              >
                <Icon name="trash" color="danger" />
              </button>
            )}
          </div>
        ),
      },
    ],
    [
      t,
      toggleModal,
      handleDelete,
      handleCopyLink,
      admissionVias,
      separateQuotaViaIds,
    ],
  );

  return (
    <>
      <div className="mt-5 mb-3">
        <div>
          <div className="fs-18 font-weight-bold text-primary text-uppercase">
            {t('management.offers')}
          </div>
          <p className="fs-16 mb-0">{t('management.offersDescription')}</p>
        </div>
      </div>
      <Table columns={columns} data={offers} />
      {showModal && (
        <OfferModal
          toggle={() => toggleModal()}
          offer={selectedOffer}
          availableOffers={availableOffers}
          admissionVias={admissionVias}
          refresh={refresh}
        />
      )}
      <div className="d-flex justify-content-end mt-3">
        {!!admissionVias?.length && (
          <Button
            text={t('bulkOffers.open')}
            size="md"
            outlined
            className="px-5 font-weight-bold mt-3 mr-3"
            onClick={() =>
              history.push(
                `/academic-offer/admission-processes/${processId}/bulk-offers`,
              )
            }
          />
        )}
        <Button
          text={t('offersTable.addOfferButton')}
          size="md"
          className="px-5 font-weight-bold mt-3"
          onClick={() => toggleModal()}
        />
      </div>
    </>
  );
}
